import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  decideFinanceRequest,
  deleteFinanceRequest,
  getFinanceRequest
} from '../../../shared/services/data/finance-request.data';
import { getCurrentUserId } from '../../../shared/services/helpers/auth-helper/auth-helper.service';
import {
  FinanceRequestDecision,
  FinanceRequestStep
} from '../../../shared/models/finance-request.model';
import {
  FinanceRequestHistoryUiModel,
  FinanceRequestSaveResultUiModel,
  FinanceRequestUiModel
} from '../../../shared/ui-models/finance-request.ui-model';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { LoadingData } from '../../../ui/LoadingData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import {
  ACTION,
  ADVANCE_STATE,
  BTN_DANGER,
  BTN_PRIMARY,
  BTN_SECONDARY,
  CHIP,
  CHIP_COLOR,
  CONTENT,
  DECLARATION,
  detailsPath,
  editPath,
  FIELDS,
  fmtDate,
  fmtWhen,
  HEADER_CHIP,
  INPUT,
  LABEL,
  LINK,
  listPath,
  money,
  newPath,
  NOTE_ATTENTION,
  NOTE_INFO,
  num,
  PAGE,
  PAGE_TITLE,
  SECTION,
  SECTION_TITLE,
  STATUS,
  STEP_LABEL,
  TABLE_WRAP,
  TD,
  TD_INPUT,
  TEXTAREA,
  TFOOT,
  TH,
  today,
  TYPES
} from '../../shared/finance-request.util';

const Item: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-[12px] font-gilroy-medium text-neutral-300">{label}</dt>
    <dd className="m-0 mt-[4px] break-words text-[15px] text-neutral-500">
      {value === undefined || value === null || value === '' ? '—' : value}
    </dd>
  </div>
);

export const RequestDetailsContainer: React.FC = () => {
  const history = useHistory();
  const { requestId } = useParams<{ requestId: string }>();
  const [r, setR] = useState<FinanceRequestUiModel | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [comment, setComment] = useState('');
  const [verified, setVerified] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | 'withdraw' | null>(null);

  const load = useCallback(() => {
    setLoadError(undefined);
    return getFinanceRequest(requestId).then((result) => {
      if (isErrorInfo(result)) {
        setLoadError(describeLoadError(result));
        return;
      }
      setR(result);
      setVerified(
        result.lines.map((l) =>
          l.verified === null || l.verified === undefined ? '' : String(l.verified)
        )
      );
      setComment('');
      setErr('');
      setConfirm(null);
    });
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) return <LoadError message={loadError} onRetry={load} />;
  if (!r) return <LoadingData />;

  const mine = r.employee.userId === getCurrentUserId();
  const cur = r.form.currency;
  const t = r.totals;
  const financeEdit = r.canAct && r.status === 'finance' && r.type === 'STL';
  const approvedTotal = financeEdit
    ? r.lines.reduce(
        (a, l, i) =>
          a + (verified[i] !== '' && verified[i] !== undefined ? num(verified[i]) : num(l.amount)),
        0
      )
    : t.approved || 0;
  const balance = financeEdit ? (t.advanced || 0) - approvedTotal : t.balance || 0;
  const overdue = r.advanceState === 'open' && !!t.settlementDue && t.settlementDue < today();
  const ticketsPath = (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH;

  let start = 0;
  r.history.forEach((h, i) => {
    if (h.action === 'submitted' || h.action === 'resubmitted') start = i;
  });
  const cycle = r.history.slice(start);
  const lastAt = (step: FinanceRequestStep) =>
    cycle.filter((h) => h.step === step).pop() as FinanceRequestHistoryUiModel | undefined;

  const act = (action: FinanceRequestDecision) => {
    if ((action === 'returned' || action === 'rejected') && !comment.trim()) {
      setErr('Add a comment explaining why.');
      return;
    }
    setBusy(true);
    setErr('');
    decideFinanceRequest(r.id, {
      action,
      comment: comment.trim(),
      verified: financeEdit ? verified.map((v) => (v === '' ? null : num(v))) : undefined
    })
      .then((result: FinanceRequestSaveResultUiModel | ErrorInfo) => {
        if (isErrorInfo(result)) throw result;
        birdToast.success(
          `${result.request.serial}: ${STATUS[result.request.status].label.toLowerCase()}`
        );
        if (result.ticketWarning) birdToast.warning(result.ticketWarning);
        if (action === 'withdrawn') return load();
        history.push(listPath('approve'));
      })
      .catch((e: ErrorInfo) => setErr(e.message || e.errorMessage))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    setBusy(true);
    deleteFinanceRequest(r.id)
      .then((result) => {
        if (isErrorInfo(result)) throw result;
        birdToast.success('Draft deleted');
        history.push(listPath());
      })
      .catch((e: ErrorInfo) => {
        setErr(e.message || e.errorMessage);
        setBusy(false);
      });
  };

  const actions = (
    <>
      {mine && r.status === 'draft' ? (
        confirm === 'delete' ? (
          <>
            <button type="button" className={BTN_DANGER} disabled={busy} onClick={remove}>
              Yes, delete draft
            </button>
            <button type="button" className={BTN_SECONDARY} onClick={() => setConfirm(null)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className={BTN_SECONDARY} onClick={() => setConfirm('delete')}>
            Delete draft
          </button>
        )
      ) : null}
      {mine && r.status === 'manager' ? (
        confirm === 'withdraw' ? (
          <>
            <button
              type="button"
              className={BTN_DANGER}
              disabled={busy}
              onClick={() => act('withdrawn')}
            >
              Yes, withdraw
            </button>
            <button type="button" className={BTN_SECONDARY} onClick={() => setConfirm(null)}>
              Keep it
            </button>
          </>
        ) : (
          <button type="button" className={BTN_SECONDARY} onClick={() => setConfirm('withdraw')}>
            Withdraw
          </button>
        )
      ) : null}
      {r.ticketId ? (
        <Link className={BTN_SECONDARY} to={ticketsPath.replace(':ticketId', String(r.ticketId))}>
          Ticket #{r.ticketId}
        </Link>
      ) : null}
      {mine && (r.status === 'draft' || r.status === 'returned') ? (
        <button type="button" className={BTN_PRIMARY} onClick={() => history.push(editPath(r.id))}>
          {r.status === 'returned' ? 'Edit & resubmit' : 'Continue editing'}
        </button>
      ) : null}
      {mine && r.type === 'ADV' && r.advanceState === 'open' ? (
        <button
          type="button"
          className={BTN_PRIMARY}
          onClick={() => history.push(newPath('STL', r.id))}
        >
          Settle this advance
        </button>
      ) : null}
    </>
  );

  return (
    <div className={PAGE}>
      <div className={`${CONTENT} pt-[32px] pb-[24px]`}>
        <button
          type="button"
          className={LINK}
          onClick={() => history.push(listPath(r.canAct ? 'approve' : undefined))}
        >
          ← Back
        </button>
        <div className="mt-[16px] flex flex-col justify-between gap-[16px] md:flex-row md:items-center">
          <div className="flex flex-wrap items-center gap-[10px]">
            <h1 className={PAGE_TITLE}>{r.serial || 'Draft'}</h1>
            <span className={`${HEADER_CHIP} ${STATUS[r.status].chip}`}>
              {STATUS[r.status].label}
            </span>
            {r.advanceState ? (
              <span
                className={`${HEADER_CHIP} ${
                  overdue ? CHIP_COLOR.LATE : ADVANCE_STATE[r.advanceState].chip
                }`}
              >
                {overdue ? 'Settlement overdue' : ADVANCE_STATE[r.advanceState].label}
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-[6px]">{actions}</div>
        </div>
        <div className="mt-[8px] flex flex-wrap gap-x-[12px] text-[15px] text-neutral-300">
          <span>{TYPES[r.type].name}</span>
          <span>·</span>
          <span>{r.employee.name}</span>
          <span>·</span>
          <span className="font-gilroy-medium text-neutral-500">
            {money(r.type === 'STL' ? balance : t.requested, cur)}
          </span>
          {r.type === 'STL' ? (
            <>
              <span>·</span>
              <span>{t.outcome}</span>
            </>
          ) : null}
        </div>
      </div>

      <div className={`${CONTENT} pb-[80px]`}>
        <div className="grid items-start gap-x-[48px] xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="divide-y divide-gray-200">
            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>1. Employee details</h2>
              <dl className={`${FIELDS} m-0`}>
                <Item label="Employee name" value={r.employee.name} />
                <Item label="Employee ID" value={r.employee.employeeId} />
                <Item label="Department" value={r.employee.department} />
                <Item label="Position" value={r.employee.position} />
                <Item label="Manager" value={r.manager && r.manager.name} />
                {r.type === 'ADV' ? (
                  <Item label="Contact / email" value={r.employee.contact} />
                ) : null}
              </dl>
            </section>

            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>
                2. {r.type === 'ADV' ? 'Advance request details' : 'Advance details'}
              </h2>
              {r.type === 'STL' ? (
                <div className={`${TABLE_WRAP} mb-[24px]`}>
                  <table className="w-full min-w-[640px] border-collapse">
                    <thead className="bg-creams-100">
                      <tr>
                        <th className={TH}>Advance reference no.</th>
                        <th className={TH}>Advance date</th>
                        <th className={TH}>Voucher / transfer no.</th>
                        <th className={`${TH} text-right`}>Amount advanced</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {r.advances.map((a, i) => (
                        <tr key={i} className="bg-neutral-0">
                          <td className={`${TD} font-gilroy-medium`}>
                            {a.requestId ? (
                              <Link className={LINK} to={detailsPath(a.requestId)}>
                                {a.serial}
                              </Link>
                            ) : (
                              a.serial || '—'
                            )}
                          </td>
                          <td className={`${TD} text-neutral-300`}>{fmtDate(a.date)}</td>
                          <td className={`${TD} text-neutral-300`}>{a.voucher || '—'}</td>
                          <td className={`${TD} text-right`}>{money(a.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              <dl className={`${FIELDS} m-0`}>
                {r.type === 'ADV' ? <Item label="Request reference no." value={r.serial} /> : null}
                {r.type === 'ADV' ? (
                  <Item label="Request date" value={r.submittedAt && fmtDate(r.submittedAt)} />
                ) : null}
                <Item
                  label={r.type === 'ADV' ? 'Purpose of trip / activity' : 'Purpose of advance'}
                  value={r.form.purpose}
                />
                <Item label="Destination / project" value={r.form.project} />
                <Item label="Cost center" value={r.form.costCenter} />
                <Item label="Period – from" value={r.form.from && fmtDate(r.form.from)} />
                <Item label="Period – to" value={r.form.to && fmtDate(r.form.to)} />
                {r.type === 'ADV' ? (
                  <Item
                    label="Date advance needed by"
                    value={r.form.needBy && fmtDate(r.form.needBy)}
                  />
                ) : null}
                <Item label="Currency" value={cur} />
                {r.type === 'ADV' ? (
                  <Item label="Preferred payment method" value={r.form.payMethod} />
                ) : null}
                {r.type === 'ADV' && r.form.payMethod === 'Bank transfer' ? (
                  <Item label="Bank account / IBAN" value={r.form.iban} />
                ) : null}
                <Item
                  label="Settlement deadline"
                  value={
                    r.form.settleDays !== undefined
                      ? `${r.form.settleDays} days after period end`
                      : ''
                  }
                />
                <Item
                  label="Settlement due date"
                  value={t.settlementDue && fmtDate(t.settlementDue)}
                />
              </dl>
            </section>

            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>
                3. {r.type === 'ADV' ? 'Estimated expenses' : 'Expense breakdown'}
              </h2>
              <div className={TABLE_WRAP}>
                <table className="w-full min-w-[760px] border-collapse">
                  <thead className="bg-creams-100">
                    <tr>
                      {r.type === 'STL' ? <th className={TH}>Date</th> : null}
                      <th className={TH}>Category</th>
                      <th className={TH}>Description</th>
                      {r.type === 'STL' ? <th className={TH}>Location</th> : null}
                      {r.type === 'STL' ? <th className={TH}>Receipt no.</th> : null}
                      <th className={`${TH} text-right`}>Amount ({cur})</th>
                      {r.type === 'ADV' ? <th className={TH}>Notes</th> : null}
                      <th className={TH}>Receipt</th>
                      {r.type === 'STL' ? (
                        <th className={`${TH} text-right`}>Verified (Finance)</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {r.lines.map((l, i) => (
                      <tr key={i} className="bg-neutral-0">
                        {r.type === 'STL' ? (
                          <td className={`${TD} whitespace-nowrap text-neutral-300`}>
                            {fmtDate(l.date)}
                          </td>
                        ) : null}
                        <td className={TD}>{l.category}</td>
                        <td className={`${TD} max-w-[240px] truncate`}>{l.description}</td>
                        {r.type === 'STL' ? (
                          <td className={`${TD} text-neutral-300`}>{l.location}</td>
                        ) : null}
                        {r.type === 'STL' ? (
                          <td className={`${TD} text-neutral-300`}>{l.receiptNo}</td>
                        ) : null}
                        <td className={`${TD} whitespace-nowrap text-right`}>{money(l.amount)}</td>
                        {r.type === 'ADV' ? (
                          <td className={`${TD} text-neutral-300`}>{l.notes}</td>
                        ) : null}
                        <td className={TD}>
                          {l.receipt ? (
                            <span className={`${CHIP} ${CHIP_COLOR.NEUTRAL}`}>Yes</span>
                          ) : r.type === 'STL' ? (
                            <span className={`${CHIP} ${CHIP_COLOR.ATTENTION}`}>Missing</span>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </td>
                        {r.type === 'STL' ? (
                          financeEdit ? (
                            <td className={`${TD_INPUT} w-[150px]`}>
                              <input
                                aria-label="Verified amount"
                                type="number"
                                step="0.01"
                                className={`${INPUT} text-right`}
                                placeholder={money(l.amount)}
                                value={verified[i] || ''}
                                onChange={(e) => {
                                  const next = [...verified];
                                  next[i] = e.target.value;
                                  setVerified(next);
                                }}
                              />
                            </td>
                          ) : (
                            <td className={`${TD} whitespace-nowrap text-right`}>
                              {l.verified !== null && l.verified !== undefined
                                ? money(l.verified)
                                : ''}
                            </td>
                          )
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className={TFOOT}>
                    <tr>
                      <td colSpan={r.type === 'ADV' ? 2 : 5} className="px-[16px] py-[12px]">
                        {r.type === 'ADV' ? 'Total estimated expenses' : 'Totals'}
                      </td>
                      <td className="px-[16px] py-[12px] text-right">
                        {money(r.type === 'ADV' ? t.estimated : t.claimed)}
                      </td>
                      {r.type === 'ADV' ? (
                        <td colSpan={2} />
                      ) : (
                        <>
                          <td />
                          <td className="px-[16px] py-[12px] text-right">{money(approvedTotal)}</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>

            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>
                4. {r.type === 'ADV' ? 'Advance summary' : 'Settlement summary'}
              </h2>
              <dl className="m-0 grid max-w-[560px] grid-cols-[1fr_auto] gap-x-[24px] gap-y-[8px] text-[14px]">
                {r.type === 'ADV' ? (
                  <>
                    <dt className="text-neutral-300">Total estimated expenses</dt>
                    <dd className="m-0 text-right">{money(t.estimated, cur)}</dd>
                    <dt className="text-neutral-300">Previous advance still unsettled</dt>
                    <dd className="m-0 text-right">{money(r.form.prevOutstanding, cur)}</dd>
                    <dt className="font-gilroy-medium">Amount requested</dt>
                    <dd className="m-0 text-right font-gilroy-medium">{money(t.requested, cur)}</dd>
                    <dt className="text-neutral-300">Check</dt>
                    <dd
                      className={`m-0 text-right ${t.check !== 'OK' ? 'text-secondary-700' : ''}`}
                    >
                      {t.check}
                    </dd>
                  </>
                ) : (
                  <>
                    <dt className="text-neutral-300">Total advance received</dt>
                    <dd className="m-0 text-right">{money(t.advanced, cur)}</dd>
                    <dt className="text-neutral-300">Total approved expenses</dt>
                    <dd className="m-0 text-right">{money(approvedTotal, cur)}</dd>
                    <dt className="font-gilroy-medium">Balance</dt>
                    <dd className="m-0 text-right font-gilroy-medium">{money(balance, cur)}</dd>
                    <dt className="text-neutral-300">Expenses without receipt</dt>
                    <dd
                      className={`m-0 text-right ${t.withoutReceipt ? 'text-secondary-700' : ''}`}
                    >
                      {t.withoutReceipt}
                    </dd>
                    {balance > 0 ? (
                      <>
                        <dt className="text-neutral-300">Cash returned</dt>
                        <dd className="m-0 text-right">
                          {[
                            r.form.returnDate && fmtDate(r.form.returnDate),
                            r.form.returnMethod,
                            r.form.returnVoucher
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </dd>
                      </>
                    ) : null}
                    {balance < 0 ? (
                      <>
                        <dt className="text-neutral-300">Reimbursement method</dt>
                        <dd className="m-0 text-right">{r.form.reimburseMethod || '—'}</dd>
                      </>
                    ) : null}
                  </>
                )}
              </dl>
            </section>

            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>
                5. {r.type === 'ADV' ? 'Employee undertaking' : 'Declaration'}
              </h2>
              <p className="m-0 mb-[20px] text-[15px] text-neutral-300">{DECLARATION[r.type]}</p>
              <dl className={`${FIELDS} m-0`}>
                <Item
                  label="Employee signature"
                  value={
                    r.acknowledgedAt
                      ? `Confirmed electronically by ${r.employee.name}`
                      : 'Not yet signed'
                  }
                />
                <Item label="Date" value={fmtWhen(r.acknowledgedAt)} />
              </dl>
            </section>

            <section className={SECTION}>
              <h2 className={SECTION_TITLE}>6. Approvals</h2>
              <div className={TABLE_WRAP}>
                <table className="w-full min-w-[640px] border-collapse">
                  <thead className="bg-creams-100">
                    <tr>
                      {['Role', 'Name', 'Comments', 'Signature', 'Date'].map((c) => (
                        <th key={c} className={TH}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(['manager', 'finance'] as FinanceRequestStep[]).map((step) => {
                      const e = lastAt(step);
                      return (
                        <tr key={step} className="bg-neutral-0">
                          <td className={`${TD} font-gilroy-medium`}>{STEP_LABEL[step]}</td>
                          <td className={TD}>
                            {e
                              ? e.by.name
                              : step === 'manager'
                              ? (r.manager && r.manager.name) || '—'
                              : 'Finance team'}
                          </td>
                          <td className={`${TD} max-w-[240px] truncate text-neutral-300`}>
                            {e ? e.comment : ''}
                          </td>
                          <td className={TD}>
                            {e ? (
                              <span className={`${CHIP} ${ACTION[e.action].chip}`}>
                                {ACTION[e.action].label}
                              </span>
                            ) : r.status === step ? (
                              <span className={`${CHIP} ${CHIP_COLOR.WAITING}`}>Pending</span>
                            ) : null}
                          </td>
                          <td className={`${TD} whitespace-nowrap text-neutral-300`}>
                            {e ? fmtWhen(e.at) : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-[24px] py-[40px] xl:sticky xl:top-0">
            {r.canAct ? (
              <section className="flex flex-col gap-[16px] rounded-[16px] border border-gray-200 p-[20px]">
                <h2 className="m-0 text-[18px] font-gilroy-bold text-neutral-500">
                  {STEP_LABEL[r.status as FinanceRequestStep] || 'Decision'}
                </h2>
                {r.type === 'ADV' && t.check && t.check !== 'OK' ? (
                  <div className={NOTE_ATTENTION}>{t.check}</div>
                ) : null}
                {r.type === 'STL' && t.withoutReceipt ? (
                  <div className={NOTE_ATTENTION}>
                    {t.withoutReceipt} expense line{t.withoutReceipt > 1 ? 's' : ''} without a
                    receipt
                  </div>
                ) : null}
                <label className={LABEL}>
                  Comment
                  <textarea
                    className={TEXTAREA}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </label>
                {err ? <div className={NOTE_ATTENTION}>{err}</div> : null}
                <div className="flex flex-wrap gap-[6px]">
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    disabled={busy}
                    onClick={() => act('approved')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className={BTN_SECONDARY}
                    disabled={busy}
                    onClick={() => act('returned')}
                  >
                    Return
                  </button>
                  <button
                    type="button"
                    className={BTN_DANGER}
                    disabled={busy}
                    onClick={() => act('rejected')}
                  >
                    Reject
                  </button>
                </div>
              </section>
            ) : null}

            {!r.canAct && mine && (r.status === 'manager' || r.status === 'finance') ? (
              <div className={NOTE_INFO}>
                Waiting for{' '}
                {r.status === 'manager'
                  ? (r.manager && r.manager.name) || 'your manager'
                  : 'Finance review'}
              </div>
            ) : null}
            {err && !r.canAct ? <div className={NOTE_ATTENTION}>{err}</div> : null}

            <section>
              <h2 className="m-0 mb-[16px] text-[18px] font-gilroy-bold text-neutral-500">
                History
              </h2>
              {r.history.length ? (
                <ol className="m-0 flex list-none flex-col gap-[16px] p-0">
                  {[...r.history].reverse().map((e, i) => (
                    <li key={i} className="flex flex-col gap-[4px]">
                      <div className="flex flex-wrap items-center gap-[8px]">
                        <span className={`${CHIP} ${ACTION[e.action].chip}`}>
                          {ACTION[e.action].label}
                        </span>
                        <span className="text-[13px] text-neutral-300">{STEP_LABEL[e.step]}</span>
                      </div>
                      <div className="text-[14px] text-neutral-500">
                        {e.by.name}{' '}
                        <span className="text-[13px] text-neutral-300">· {fmtWhen(e.at)}</span>
                      </div>
                      {e.comment ? (
                        <q className="block whitespace-pre-wrap text-[14px] text-neutral-300">
                          {e.comment}
                        </q>
                      ) : null}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="m-0 text-[14px] text-neutral-300">Not submitted yet</p>
              )}
            </section>

            {r.type === 'ADV' && r.settlements.length > 0 ? (
              <section className="border-t border-gray-200 pt-[24px]">
                <h2 className="m-0 mb-[16px] text-[18px] font-gilroy-bold text-neutral-500">
                  Settlements
                </h2>
                <div className="flex flex-col gap-[10px]">
                  {r.settlements.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-[8px]">
                      <Link className={LINK} to={detailsPath(s.id)}>
                        {s.serial || 'Draft'}
                      </Link>
                      <span className={`${CHIP} ${STATUS[s.status].chip}`}>
                        {STATUS[s.status].label}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
};
